// Bubble Shooter — Blasen auf das Hex-Raster schießen, ab 3 gleichen Farben
// platzt die Gruppe. Alle paar Schüsse rückt oben eine neue Reihe nach.
// Pixel-Look wie die anderen Spiele, alles prozedural gezeichnet.
import { useRef, useEffect } from "react";
import { drawSprite, drawBlockText, drawCoin, setupCanvas } from "./pixelArt";

const H = 640;
const BASE_ASPECT = 420 / 640;
const D = 36; // Blasen-Durchmesser
const R = D / 2;
const ROW_H = 31; // Zeilenhöhe im Hex-Raster (~D * 0.866)
const TOP = 64; // Oberkante des Rasters (unter dem Score)
const START_ROWS = 5;
const SHOTS_PER_ROW = 8; // danach rückt eine neue Reihe nach
const SHOT_SPEED = 720;
const DEADLINE_Y = H - 150;
const COIN_BUBBLE_CHANCE = 0.02; // selten ist eine Blase eine Münze

const COLORS = [
  { b: "#d93a2b", l: "#f06451", d: "#a82417" }, // Rot
  { b: "#57b9e8", l: "#7ecdf2", d: "#3d97c4" }, // Blau
  { b: "#e8c34e", l: "#f2d67a", d: "#c4a136" }, // Gelb
  { b: "#6abe30", l: "#7ed641", d: "#4e9420" }, // Grün
  { b: "#9b6ee8", l: "#b48ff2", d: "#7a4ec4" }, // Lila
];

// Eigenkreation: blockige Blase mit Glanzpunkt oben links
const BUBBLE_SPRITE = [
  "..bbbb..",
  ".bllbbb.",
  "bllbbbbd",
  "blbbbbbd",
  "bbbbbbbd",
  "bbbbbbdd",
  ".bbbbdd.",
  "..bddd..",
];

export default function BubbleShooterGame({ onGameOver, onStart, autoStart = false }) {
  const canvasRef = useRef(null);
  const onGameOverRef = useRef(onGameOver);
  onGameOverRef.current = onGameOver;
  const onStartRef = useRef(onStart);
  onStartRef.current = onStart;

  useEffect(() => {
    const canvas = canvasRef.current;
    // Spielfeldbreite ans Seitenverhältnis des Containers anpassen: auf dem
    // Desktop wird das Raster breiter (mehr Spalten), nichts wird verzerrt.
    const availW = canvas.parentElement?.clientWidth || 420;
    const availH = canvas.parentElement?.clientHeight || H;
    const aspect = Math.min(Math.max(availW / availH, BASE_ASPECT), 3.2);
    const W = Math.round(H * aspect);
    const ctx = setupCanvas(canvas, W, H);

    const cols = Math.floor((W - R) / D);
    const gridW = cols * D + R; // versetzte Reihen ragen R nach rechts
    const offsetX = (W - gridW) / 2;
    const leftWall = offsetX;
    const rightWall = offsetX + gridW;
    const shooter = { x: W / 2, y: H - 78 };

    const state = {
      phase: "ready", // ready | running | over
      rows: [], // [row][col] = null | { type: "color", color } | { type: "coin" }
      parity: 0, // 0 = Reihe 0 nicht versetzt
      angle: -Math.PI / 2,
      cur: 0, // Farbe der geladenen Blase
      next: 0,
      shot: null, // fliegende Blase { x, y, vx, vy, color }
      shotsFired: 0,
      score: 0,
      coins: 0,
      lastTime: 0,
    };

    const shifted = (r) => (r + state.parity) % 2 === 1;
    const bx = (r, c) => offsetX + c * D + (shifted(r) ? R : 0) + R;
    const by = (r) => TOP + r * ROW_H + R;

    const makeBubble = () =>
      Math.random() < COIN_BUBBLE_CHANCE
        ? { type: "coin" }
        : { type: "color", color: Math.floor(Math.random() * COLORS.length) };

    const makeRow = () => Array.from({ length: cols }, makeBubble);

    // Farben, die noch auf dem Feld liegen (damit man nie Unbrauchbares lädt)
    const activeColors = () => {
      const set = new Set();
      for (const row of state.rows) {
        for (const cell of row) {
          if (cell && cell.type === "color") set.add(cell.color);
        }
      }
      return set.size > 0 ? [...set] : COLORS.map((_, i) => i);
    };

    const randomShotColor = () => {
      const options = activeColors();
      return options[Math.floor(Math.random() * options.length)];
    };

    // Nachbarn im versetzten Hex-Raster
    const neighbors = (r, c) => {
      const side = shifted(r) ? 1 : -1;
      return [
        [r, c - 1],
        [r, c + 1],
        [r - 1, c],
        [r - 1, c + side],
        [r + 1, c],
        [r + 1, c + side],
      ].filter(([nr, nc]) => nr >= 0 && nc >= 0 && nc < cols);
    };

    const cellAt = (r, c) => (state.rows[r] ? state.rows[r][c] : null) || null;

    const ensureRows = (upTo) => {
      while (state.rows.length <= upTo) {
        state.rows.push(Array(cols).fill(null));
      }
    };

    // Schwebende Gruppen entfernen: alles, was nicht (über Nachbarn) an der
    // obersten Reihe hängt, fällt herunter
    const dropFloating = () => {
      const seen = new Set();
      const stack = [];
      for (let c = 0; c < cols; c++) {
        if (cellAt(0, c)) {
          seen.add(`0,${c}`);
          stack.push([0, c]);
        }
      }
      while (stack.length) {
        const [r, c] = stack.pop();
        for (const [nr, nc] of neighbors(r, c)) {
          const k = `${nr},${nc}`;
          if (!seen.has(k) && cellAt(nr, nc)) {
            seen.add(k);
            stack.push([nr, nc]);
          }
        }
      }
      let dropped = 0;
      for (let r = 0; r < state.rows.length; r++) {
        for (let c = 0; c < cols; c++) {
          const cell = cellAt(r, c);
          if (!cell || seen.has(`${r},${c}`)) continue;
          if (cell.type === "coin") state.coins++;
          else dropped++;
          state.rows[r][c] = null;
        }
      }
      return dropped;
    };

    // Nach dem Andocken: gleiche Farben suchen, ab 3 platzt die Gruppe.
    // Münz-Blasen direkt neben der Gruppe werden mit eingesammelt.
    const resolveMatches = (r0, c0) => {
      const placed = cellAt(r0, c0);
      if (!placed || placed.type !== "color") return;
      const group = [];
      const seen = new Set([`${r0},${c0}`]);
      const stack = [[r0, c0]];
      while (stack.length) {
        const [r, c] = stack.pop();
        group.push([r, c]);
        for (const [nr, nc] of neighbors(r, c)) {
          const k = `${nr},${nc}`;
          const cell = cellAt(nr, nc);
          if (!seen.has(k) && cell && cell.type === "color" && cell.color === placed.color) {
            seen.add(k);
            stack.push([nr, nc]);
          }
        }
      }
      if (group.length < 3) return;
      for (const [r, c] of group) {
        for (const [nr, nc] of neighbors(r, c)) {
          const cell = cellAt(nr, nc);
          if (cell && cell.type === "coin") {
            state.coins++;
            state.rows[nr][nc] = null;
          }
        }
        state.rows[r][c] = null;
      }
      state.score += group.length;
      state.score += dropFloating();
      // Feld leergeräumt → Bonus und frischer Nachschub
      if (!state.rows.some((row) => row.some(Boolean))) {
        state.score += 25;
        state.rows = [];
        for (let i = 0; i < 4; i++) state.rows.push(makeRow());
      }
    };

    const lowestBubbleY = () => {
      let max = -Infinity;
      for (let r = 0; r < state.rows.length; r++) {
        if (state.rows[r].some(Boolean)) max = by(r) + R;
      }
      return max;
    };

    const die = () => {
      state.phase = "over";
      if (onGameOverRef.current)
        onGameOverRef.current(state.score, state.coins);
    };

    const checkDeadline = () => {
      if (lowestBubbleY() > DEADLINE_Y) die();
    };

    const insertRow = () => {
      state.rows.unshift(makeRow());
      state.parity = 1 - state.parity;
      checkDeadline();
    };

    // Fliegende Blase an die nächste freie Rasterzelle andocken
    const snapShot = () => {
      const s = state.shot;
      const rGuess = Math.max(0, Math.round((s.y - TOP - R) / ROW_H));
      let best = null;
      let bestDist = Infinity;
      for (let r = Math.max(0, rGuess - 2); r <= rGuess + 2; r++) {
        ensureRows(r);
        for (let c = 0; c < cols; c++) {
          if (state.rows[r][c]) continue;
          const dx = bx(r, c) - s.x;
          const dy = by(r) - s.y;
          const d = dx * dx + dy * dy;
          if (d < bestDist) {
            bestDist = d;
            best = [r, c];
          }
        }
      }
      state.shot = null;
      if (!best) return;
      const [r, c] = best;
      state.rows[r][c] = { type: "color", color: s.color };
      resolveMatches(r, c);
      state.shotsFired++;
      if (state.shotsFired % SHOTS_PER_ROW === 0) insertRow();
      checkDeadline();
    };

    const update = (dt) => {
      const s = state.shot;
      if (!s) return;
      s.x += s.vx * dt;
      s.y += s.vy * dt;
      // Wände: abprallen
      if (s.x - R < leftWall) {
        s.x = leftWall + R;
        s.vx = Math.abs(s.vx);
      }
      if (s.x + R > rightWall) {
        s.x = rightWall - R;
        s.vx = -Math.abs(s.vx);
      }
      // Decke erreicht?
      if (s.y - R <= TOP) {
        snapShot();
        return;
      }
      // Berührt eine liegende Blase?
      for (let r = 0; r < state.rows.length; r++) {
        for (let c = 0; c < cols; c++) {
          if (!state.rows[r][c]) continue;
          const dx = bx(r, c) - s.x;
          const dy = by(r) - s.y;
          if (dx * dx + dy * dy < (D - 6) * (D - 6)) {
            snapShot();
            return;
          }
        }
      }
    };

    const shoot = () => {
      if (state.shot) return; // immer nur eine Blase unterwegs
      state.shot = {
        x: shooter.x,
        y: shooter.y,
        vx: Math.cos(state.angle) * SHOT_SPEED,
        vy: Math.sin(state.angle) * SHOT_SPEED,
        color: state.cur,
      };
      state.cur = state.next;
      state.next = randomShotColor();
    };

    const reset = () => {
      state.rows = [];
      for (let i = 0; i < START_ROWS; i++) state.rows.push(makeRow());
      state.parity = 0;
      state.angle = -Math.PI / 2;
      state.shot = null;
      state.shotsFired = 0;
      state.score = 0;
      state.coins = 0;
      state.cur = randomShotColor();
      state.next = randomShotColor();
    };

    const start = () => {
      reset();
      state.phase = "running";
      if (onStartRef.current) onStartRef.current();
    };

    // Zeiger-Koordinaten in logische Canvas-Koordinaten umrechnen
    // (object-fit: contain kann Letterboxing erzeugen)
    const toLocal = (e) => {
      const rect = canvas.getBoundingClientRect();
      const scale = Math.min(rect.width / W, rect.height / H);
      const offX = rect.left + (rect.width - W * scale) / 2;
      const offY = rect.top + (rect.height - H * scale) / 2;
      return { x: (e.clientX - offX) / scale, y: (e.clientY - offY) / scale };
    };

    const aimAt = (p) => {
      const angle = Math.atan2(p.y - shooter.y, p.x - shooter.x);
      // Nur nach oben zielen (10°–170°)
      state.angle = Math.min(-0.18, Math.max(-Math.PI + 0.18, angle));
    };

    const onPointerMove = (e) => {
      if (state.phase === "running") aimAt(toLocal(e));
    };
    const onPointerDown = (e) => {
      e.preventDefault();
      if (state.phase !== "running") {
        start();
        return;
      }
      aimAt(toLocal(e));
      shoot();
    };
    const onKeyDown = (e) => {
      if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        e.preventDefault();
        if (state.phase !== "running") return;
        const step = e.key === "ArrowLeft" ? -0.06 : 0.06;
        state.angle = Math.min(-0.18, Math.max(-Math.PI + 0.18, state.angle + step));
      }
      if (e.key === " " || e.key === "Enter") {
        e.preventDefault();
        if (state.phase === "running") shoot();
        else start();
      }
    };

    const drawBubbleAt = (x, y, cell, size = D) => {
      if (cell.type === "coin") {
        drawCoin(ctx, x - size / 2, y - size / 2, size);
        return;
      }
      drawSprite(ctx, BUBBLE_SPRITE, COLORS[cell.color], x - size / 2, y - size / 2, size / 8);
    };

    const draw = () => {
      const grad = ctx.createLinearGradient(0, 0, 0, H);
      grad.addColorStop(0, "#131c30");
      grad.addColorStop(1, "#0b0c0f");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, W, H);

      // Seitenwände
      ctx.fillStyle = "#1c2733";
      ctx.fillRect(leftWall - 8, 0, 8, H);
      ctx.fillRect(rightWall, 0, 8, H);

      // Liegende Blasen
      for (let r = 0; r < state.rows.length; r++) {
        for (let c = 0; c < cols; c++) {
          const cell = state.rows[r][c];
          if (cell) drawBubbleAt(bx(r, c), by(r), cell);
        }
      }

      // Deadline (gestrichelt)
      ctx.fillStyle = "rgba(217, 58, 43, 0.5)";
      for (let x = leftWall; x < rightWall; x += 24) {
        ctx.fillRect(x, DEADLINE_Y, 12, 3);
      }

      // Zielhilfe (gepunktete Linie)
      if (state.phase === "running" && !state.shot) {
        ctx.fillStyle = "rgba(255,255,255,0.5)";
        for (let i = 1; i <= 9; i++) {
          const px = shooter.x + Math.cos(state.angle) * i * 22;
          const py = shooter.y + Math.sin(state.angle) * i * 22;
          if (py < TOP) break;
          ctx.fillRect(px - 2, py - 2, 4, 4);
        }
      }

      // Fliegende Blase
      if (state.shot) {
        drawBubbleAt(state.shot.x, state.shot.y, { type: "color", color: state.shot.color });
      }

      // Abschussrampe: Sockel + geladene Blase + nächste Blase klein daneben
      ctx.fillStyle = "#1c2733";
      ctx.fillRect(shooter.x - 26, shooter.y + R - 2, 52, 26);
      ctx.fillStyle = "#2a3a50";
      ctx.fillRect(shooter.x - 18, shooter.y + R + 2, 36, 18);
      drawBubbleAt(shooter.x, shooter.y, { type: "color", color: state.cur });
      drawBubbleAt(shooter.x + 54, shooter.y + R, { type: "color", color: state.next }, D * 0.65);

      // HUD
      drawBlockText(ctx, `${state.score}`, W / 2, 34, 32);
      drawCoin(ctx, 12, 16, 20);
      drawBlockText(ctx, `${state.coins}`, 48, 27, 20);

      if (state.phase === "ready") {
        ctx.fillStyle = "rgba(8, 12, 20, 0.55)";
        ctx.fillRect(0, 0, W, H);
        drawBlockText(ctx, "BUBBLES", W / 2, H / 2 - 40, 46);
        drawBlockText(ctx, "Tippen zum Start", W / 2, H / 2 + 10, 16);
      } else if (state.phase === "over") {
        ctx.fillStyle = "rgba(8, 12, 20, 0.55)";
        ctx.fillRect(0, 0, W, H);
        drawBlockText(ctx, "GAME OVER", W / 2, H / 2 - 40, 40);
        drawBlockText(ctx, `${state.score} Blasen`, W / 2, H / 2 + 5, 24);
        drawBlockText(ctx, "Tippen für Neustart", W / 2, H / 2 + 45, 15);
      }
    };

    let raf;
    const loop = (now) => {
      const dt = Math.min(0.04, (now - state.lastTime) / 1000);
      state.lastTime = now;
      if (state.phase === "running") update(dt);
      draw();
      raf = requestAnimationFrame(loop);
    };

    window.addEventListener("keydown", onKeyDown);
    canvas.addEventListener("pointerdown", onPointerDown);
    canvas.addEventListener("pointermove", onPointerMove);

    reset();
    state.lastTime = performance.now();
    if (autoStart) start(); // Start-Button auf der Seite: direkt losspielen
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("keydown", onKeyDown);
      canvas.removeEventListener("pointerdown", onPointerDown);
      canvas.removeEventListener("pointermove", onPointerMove);
    };
  }, [autoStart]);

  return (
    <canvas
      ref={canvasRef}
      className="w-full h-full touch-none select-none"
      style={{ imageRendering: "pixelated", objectFit: "contain" }}
    />
  );
}
